/**
 * Paint kernels — what one dab of the brush does to the ground's paint map.
 *
 * Pure functions over a {@link SurfaceMap}: given a dab centre, a brush, and a mode, they mutate the
 * bytes and report the texel rectangle they touched. The tool layer above handles pointers and
 * history; nothing here knows either exists, which is what makes the mechanics testable in isolation.
 *
 * ## Flow, opacity, and why a stroke needs a memory
 *
 * Two knobs that look like one. **Flow** is how much a single dab lays down; **opacity** is the most
 * one continuous stroke may ever reach. With dabs spaced a quarter-radius apart, a point near the
 * middle of a stroke is covered by a dozen of them, so flow alone would saturate everything to 100 %
 * the moment the brush passed over — the softness of the falloff would survive only at the very
 * outermost rim, and a "50 % mulch" wash would be impossible.
 *
 * The fix is that a stroke remembers what it has already laid down, per texel, in
 * {@link PaintStrokeState}. Each dab may only top a texel up toward the opacity ceiling, never past
 * it. Release the button and the memory clears, so a second pass over the same ground builds further
 * — which is exactly how a person expects a brush to behave, and it is also what makes overlapping
 * passes within one stroke look even instead of blotchy.
 */

import {
  clipRect,
  compositeLayerWeight,
  EMPTY_RECT,
  getSurfaceLayer,
  isEmptyRect,
  paintedWeightAt,
  type SurfaceLayerId,
  type SurfaceMap,
  type SurfaceRect,
  texelToWorld,
} from './TerrainSurface';
import { brushFalloff, dabGridBounds, type BrushPoint, type BrushShape } from './TerrainBrush';

/**
 * What the brush is doing to the paint.
 *
 * - `paint` lays down the active layer.
 * - `erase` takes every layer back out, revealing grass. Painting grass and erasing are the same
 *   operation, which is why grass has no channel of its own.
 * - `smooth` pulls each texel toward its neighbourhood average, softening a hard boundary without
 *   moving it. The tool that draws a bed edge also has to be able to feather it.
 */
export type PaintMode = 'paint' | 'erase' | 'smooth';

/**
 * The per-stroke accumulation buffer. One byte per texel: the coverage this stroke has *achieved*
 * there so far, on the same 0–255 scale as the weights.
 *
 * Achieved coverage, not the sum of what was applied — those differ, and the difference is the whole
 * point. Two dabs of 30 % do not produce 60 % coverage; they produce `1 - 0.7²` = 51 %, because the
 * second lands on ground the first already covered. Capping the *sum* at the opacity would therefore
 * stop the stroke short of the coverage the user asked for, by an amount that varies with the flow
 * setting. Tracking the composited result instead means an opacity of 50 % ends at 50 %.
 */
export class PaintStrokeState {
  readonly applied: Uint8Array;

  constructor(texelCount: number) {
    this.applied = new Uint8Array(texelCount);
  }

  /** Begin a new stroke — forget everything the last one laid down. */
  reset(): void {
    this.applied.fill(0);
  }
}

export interface PaintDabOptions {
  /** Which layer `paint` lays down. Ignored by `erase` and `smooth`. */
  layer: SurfaceLayerId;
  mode: PaintMode;
  /** Per-stroke memory. Omit for a one-shot dab with no accumulation ceiling. */
  stroke?: PaintStrokeState;
}

/**
 * Apply one dab to the map. Returns the texel rectangle actually modified, empty when the dab fell
 * outside the site or changed nothing.
 *
 * `center` is in **terrain-local** metres — the caller unrotates world coordinates by the site's
 * north orientation first, so a rotated site paints straight.
 */
export function applyPaintDab(
  map: SurfaceMap,
  center: BrushPoint,
  brush: BrushShape,
  options: PaintDabOptions,
): SurfaceRect {
  const bounds = clipRect(
    dabGridBounds(
      center,
      brush.radiusMeters,
      map.width,
      map.height,
      map.widthMeters,
      map.lengthMeters,
    ),
    map.width,
    map.height,
  );
  if (isEmptyRect(bounds)) return { ...EMPTY_RECT };

  if (options.mode === 'smooth') {
    return smoothDab(map, center, brush, bounds);
  }

  const channel = options.mode === 'erase' ? -1 : getSurfaceLayer(options.layer).channel;
  const ceiling = 255 * Math.min(1, Math.max(0, brush.opacity));
  const flow = Math.min(1, Math.max(0, brush.flow));
  let touched = false;
  let minX = bounds.x + bounds.width;
  let minY = bounds.y + bounds.height;
  let maxX = bounds.x;
  let maxY = bounds.y;

  for (let row = bounds.y; row < bounds.y + bounds.height; row += 1) {
    for (let col = bounds.x; col < bounds.x + bounds.width; col += 1) {
      const world = texelToWorld(map, col, row);
      const distance = Math.hypot(world.x - center.x, world.z - center.z);
      const falloff = brushFalloff(distance, brush.radiusMeters, brush.hardness);
      if (falloff <= 0) continue;

      const index = row * map.width + col;
      let weight = falloff * flow;
      if (options.stroke) {
        const already = options.stroke.applied[index] ?? 0;
        const headroom = 255 - already;
        if (headroom <= 0) continue;
        // Where this dab would take the stroke's coverage, composited over what it already has.
        const reached = Math.min(ceiling, already + headroom * weight);
        if (reached <= already) continue;
        // Re-derive the weight that gets from `already` to `reached`, which is what the ceiling may
        // have shortened.
        weight = (reached - already) / headroom;
        options.stroke.applied[index] = Math.round(reached);
      }
      if (weight <= 0) continue;

      compositeLayerWeight(map, index, channel, weight);
      touched = true;
      if (col < minX) minX = col;
      if (col > maxX) maxX = col;
      if (row < minY) minY = row;
      if (row > maxY) maxY = row;
    }
  }

  if (!touched) return { ...EMPTY_RECT };
  map.touch();
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
}

/**
 * Pull each texel toward the average of its 3×3 neighbourhood, weighted by the brush falloff.
 *
 * Reads from a snapshot of the region rather than from the live bytes: smoothing in place would let
 * an already-smoothed texel feed the next one, which smears the result in whatever direction the
 * loop happens to run. The snapshot costs one small allocation per dab and makes the operation
 * direction-independent.
 *
 * The stroke's accumulation ceiling does not apply here — smoothing is idempotent-ish by nature
 * (repeated passes converge on the local average rather than running away), so capping it would just
 * make the tool stop responding mid-gesture.
 */
function smoothDab(
  map: SurfaceMap,
  center: BrushPoint,
  brush: BrushShape,
  bounds: SurfaceRect,
): SurfaceRect {
  const source = map.readRect(bounds);
  const plane = bounds.width * bounds.height * 4;
  const rate = Math.min(1, Math.max(0, brush.flow));
  let touched = false;

  const sampleChannel = (col: number, row: number, page: number, channel: number): number => {
    const c = Math.min(bounds.width - 1, Math.max(0, col - bounds.x));
    const r = Math.min(bounds.height - 1, Math.max(0, row - bounds.y));
    return source[page * plane + (r * bounds.width + c) * 4 + channel] ?? 0;
  };

  for (let row = bounds.y; row < bounds.y + bounds.height; row += 1) {
    for (let col = bounds.x; col < bounds.x + bounds.width; col += 1) {
      const world = texelToWorld(map, col, row);
      const distance = Math.hypot(world.x - center.x, world.z - center.z);
      const falloff = brushFalloff(distance, brush.radiusMeters, brush.hardness);
      if (falloff <= 0) continue;

      const blend = falloff * rate;
      const index = row * map.width + col;
      for (let page = 0; page < map.pages; page += 1) {
        const offset = map.offset(index, page);
        for (let channel = 0; channel < 4; channel += 1) {
          let total = 0;
          for (let dy = -1; dy <= 1; dy += 1) {
            for (let dx = -1; dx <= 1; dx += 1) {
              total += sampleChannel(col + dx, row + dy, page, channel);
            }
          }
          const average = total / 9;
          const current = map.data[offset + channel] ?? 0;
          map.data[offset + channel] = Math.round(current + (average - current) * blend);
        }
      }
      touched = true;
    }
  }

  if (!touched) return { ...EMPTY_RECT };
  map.touch();
  return { ...bounds };
}

/**
 * Fill every texel whose dominant weight is the base layer with `layer`, at full strength.
 *
 * The "flood the lawn" convenience: a site that is meant to be entirely gravel should not have to be
 * painted by hand. Deliberately refuses to overwrite ground that already carries paint, so it is a
 * background fill rather than a destructive reset.
 */
export function fillUnpainted(map: SurfaceMap, layer: SurfaceLayerId): SurfaceRect {
  const channel = getSurfaceLayer(layer).channel;
  if (channel < 0) return { ...EMPTY_RECT };
  for (let i = 0; i < map.texelCount; i += 1) {
    if (paintedWeightAt(map, i) >= 128) continue;
    compositeLayerWeight(map, i, channel, 1);
  }
  map.touch();
  return { x: 0, y: 0, width: map.width, height: map.height };
}
